import * as React from "react";
import { WithStyles, withStyles, createStyles, Theme } from 'components/controls/styles';
import { RxFormFieldDefs } from "components/rx-form";
import { withActionsExecutor, ActionsExecutorPropsType, ActionError, ValidationErrors } from "components/actions";
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { Typography } from "components/controls/widget";
import { FormSpecification, FormResponse, apiPutResponse, apiGetResponse, apiPostFileUrl, RespField, Response, FormResponseFile } from "common/api/Forms";
import { FileInfo } from "components/controls/file-uploader/FileInfo";
import VotingForm, { VotingFormProps } from "./VotingForm";
import buildFormFields from "./buildFormFields";


interface VotingPageProps {
    show: boolean;
    formSpec: FormSpecification;
    idtClena: number;
    onClose: (formResp?: FormResponse) => void;
}

interface State {
    loading: boolean;
    hasError: boolean;
    error?: string;
    formResp?: FormResponse;
}

type PropsType = VotingPageProps & State & ActionsExecutorPropsType & WithStyles<typeof styles>;

const initialState: State = {
    loading: true,
    hasError: false,
}


class VotingPage extends React.PureComponent<PropsType> {

    fields: RxFormFieldDefs;

    constructor(props: PropsType) {
        super(props);

        this.fields = buildFormFields(this.props.formSpec.specification);
    }

    load = (specificationId: number, idtClena: number) => {

        const { executeAction, updateState, formResp, hasError } = this.props;

        const action = apiGetResponse(specificationId, idtClena)
            .onSuccess(d => {
                updateState({ loading: false, formResp: d });
            })
            .onError(e => {
                const errMessage = e.message;
                updateState({ loading: false, hasError: true, error: errMessage });
            });


        executeAction(action, { loading: true, hasError: false, formResp: undefined });
    }


    getFileInfo = (f: FormResponseFile): FileInfo => {
        const { formSpec, idtClena } = this.props;

        const result: FileInfo = {
            fileId: f.fileId,
            name: f.fileName,
            upload: undefined,
            uploaded: true,
            uploading: false,
            hasError: false,
            progress: 0,
        };

        return result;
    }

    getFiles = (data: { [key: string]: any }, fieldName: string): FormResponseFile[] => {
        const { formSpec, idtClena } = this.props;

        return (data[fieldName] || []).map((f: FileInfo) => {
            const result: FormResponseFile = {
                specificationId: formSpec.id,
                memberId: idtClena,
                item: fieldName,
                fileId: f.fileId,
                fileName: f.name,
            };
            return result
        });
    }

    save = (data: { [key: string]: any }, success: () => void, failure: (errors: { [key: string]: string }) => void) => {

        const { formSpec, formResp, executeAction, updateState } = this.props;

        const response: Response = { fields: [] };
        let files: FormResponseFile[] = [];

        if (formSpec.specification.cisloDivize === 0) {
            files = this.getFiles(data, 'doklad_totoznosti');

            for (let i = 1; i <= 22; i++) {
                const fname = `hlasovani_${i}`;
                response.fields.push({ name: fname, values: data[fname] });
            }


        } else {
            files = this.getFiles(data, 'doklad_totoznosti')
                .concat(this.getFiles(data, 'plne_moci'));

            response.fields.push({ name: 'typ_hlasovani', value: data['typ_hlasovani'] });
            response.fields.push({ name: 'volba_delegatu', values: data['volba_delegatu'] });
            response.fields.push({ name: 'pocet_hlasu', value: data['pocet_hlasu'] });
        }

        const formResponse: FormResponse = {
            ...formResp,
            completed: data['completed'] === true,
            files: files,
            response: response,
        }

        const action = apiPutResponse(formSpec.id.toString(), formResp.memberId.toString())
            .onSuccess(d => {
                success();
                this.props.onClose(d);
            })
            .onError(e => this.handleSubmitError(e, failure));

        executeAction(action, {}, formResponse);

    }


    handleSubmitError = (e: ActionError, failure: (errors: ValidationErrors) => void) => {
        if (e.errorData && e.errorData.validationError) {
            if (e.errorData.validationErrors['_error'] === undefined) {
                e.errorData.validationErrors['_error'] = 'Formulář obsahuje chyby!';
            }
            failure(e.errorData.validationErrors);
        } else {
            failure({ '_error': e.message });
        }
    }

    cancel = () => {
        this.props.onClose();
    }

    onEnter = () => {
        const { formSpec, idtClena } = this.props;
        this.load(formSpec.id, idtClena);
    }

    getDefaultValues = () => {
        const { formResp } = this.props;


        const defaultValues: { [key: string]: any } = {};
        if (formResp !== undefined) {

            formResp.response.fields.forEach((x: RespField) => (defaultValues[x.name] = x.value || x.values));
            (formResp.files).forEach((x: FormResponseFile) => (defaultValues[x.item] = (defaultValues[x.item] || []).concat(this.getFileInfo(x))));
        }

        return defaultValues;
    }

    render() {
        const { formSpec, idtClena, loading, hasError, error, show } = this.props;
        const { classes, ...other } = this.props;

        const uploading = false;


        const votingFormProps: VotingFormProps = {
            ...other,
            fields: this.fields,
            uploadFileUrl: apiPostFileUrl(formSpec.id, idtClena),
            cancel: this.cancel,
            disableValidation: true,
        }

        return <Dialog open={show} fullWidth maxWidth="md" onEnter={this.onEnter}>
            <DialogTitle disableTypography className={classes.titleRoot}>
                <Typography variant="h6">{formSpec.name}</Typography>
                <IconButton aria-label="Close" className={classes.titleCloseButton} onClick={this.cancel} disabled={uploading}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <VotingForm {...votingFormProps} formName="VotingForm" onSubmit={this.save} defaultValues={this.getDefaultValues()} />
        </Dialog>;
    }
}


const styles = (theme: Theme) => createStyles({
    titleRoot: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        margin: 0,
        padding: theme.spacing.unit * 2,
    },
    titleCloseButton: {
        position: 'absolute',
        right: theme.spacing.unit,
        top: theme.spacing.unit,
        color: theme.palette.grey[500],
    },
});

export default withActionsExecutor<VotingPageProps, State>(initialState, withStyles(styles)(VotingPage));


